import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { LoaderCircle, Search } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";

interface SearchInputProps extends Omit<React.ComponentProps<"input">, "type"> {
  /** Controlled loading state. If undefined, auto-detects based on value changes */
  isLoading?: boolean;
  /** Icon size in pixels */
  iconSize?: number;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value, isLoading: externalLoading, iconSize = 16, onChange, ...props }, ref) => {
    const [internalLoading, setInternalLoading] = useState(false);
    
    // Auto-detect loading state based on value changes
    useEffect(() => {
      if (externalLoading !== undefined) return; // Use external loading if provided
      
      if (value) {
        setInternalLoading(true);
        const timer = setTimeout(() => {
          setInternalLoading(false);
        }, 400);
        return () => clearTimeout(timer);
      }
      setInternalLoading(false);
    }, [value, externalLoading]);

    const isLoading = externalLoading ?? internalLoading;

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="search"
          value={value}
          onChange={onChange}
          className={cn("peer ps-9 border-border shadow-sm", className)}
          style={{ background: 'hsl(var(--muted) / 0.4)' }}
          {...props}
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
          {isLoading ? (
            <LoaderCircle
              className="animate-spin"
              size={iconSize}
              strokeWidth={2}
              role="status"
              aria-label="Searching..."
            />
          ) : (
            <Search size={iconSize} strokeWidth={2} aria-hidden="true" />
          )}
        </div>
      </div>
    );
  }
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
